import { inject } from 'inversify';
import { DateTime } from 'luxon';
import { ComputedRef, computed } from 'vue';

import type { ITrip } from '../../types/api/ITrip';
import type { ITripListRespMsg } from '../../types/api/messages/ITripListRespMsg';
import type { ITripUpdateMsg } from '../../types/api/messages/ITripUpdateMsg';

import { AttachmentManager } from './AttachmentManager';
import { ProQAScriptManager } from './ProQAScriptManager';
import { TableManager } from './TableManager';
import { assignUnitToTrip, AssignUnitToTripParams } from './trip/assignUnitToTrip';
import { cancelTrip, CancelTripParams } from './trip/cancelTrip';
import { changeUnitStatus, ChangeUnitStatusParams } from './trip/changeUnitStatus';
import { checkForRequiredLateReasons } from './trip/checkForRequiredLateReasons';
import { createRecurringTrip, CreateRecurringTripParams } from './trip/createRecurringTrip';
import { createReturnTrip, CreateReturnTripParams } from './trip/createReturnTrip';
import { createTrip, CreateTripParams } from './trip/createTrip';
import { duplicateTrip, DuplicateTripParams } from './trip/duplicateTrip';
import { tryTripCreateAssignAction } from './trip/tryTripCreateAssignAction';
import { tryUnitAction, ITripActionData } from './trip/tryUnitAction';
import { unassignTrip, UnassignTripParams } from './trip/unassignTrip';
import { updateLateReason } from './trip/updateLateReason';
import { updatePatientWithTrip, UpdatePatientWithTripParams } from './trip/updatePatientWithTrip';
import { updateTripTimestamps, UpdateTripTimestampsParams } from './trip/updateTripTimestamps';

import {
  APP, FILTER_FIELD, TABLE, TRIPS,
} from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { Trip } from '@/entities/Trip';
import constants from '@/helpers/constants';
import { ConfigurationManager } from '@/managers/ConfigurationManager';
import { Connection } from '@/services/Connection';
import { FilterService } from '@/services/FilterService';
import { Logger } from '@/services/Logger';
import { TripListReqMsg } from '@/services/signalr/MessageTypes/TripList';
import { Signalr } from '@/services/signalr/Signalr';
import { TripStore } from '@/stores/TripStore';
import { VehiclePositionStore } from '@/stores/VehiclePositionStore';
import { belongsToUnitShift } from '@/utils/belongsToUnitShift';
import { normalize, normalizeObjects } from '@/utils/normalize';
import { consumeStatusChangeTiming } from '@/utils/statusChangeTimings';
import {
  isComplete, isCanceled, isAssignedOrPreAssigned, isUnassigned,
} from '@/utils/tripStatusUtils';

type DispatchCallback = (action: string, payload: unknown) => Promise<unknown>;

// Server sends naive ISO strings for pickupDateTime that represent UTC. Date.parse
// treats them as local time per the ECMAScript spec — append 'Z' to force UTC,
// matching DateTime.fromISO(..., { zone: 'utc' }) used elsewhere in the codebase.
const parseUtc = (iso: string): number => {
  const hasTz = iso.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(iso);
  return Date.parse(hasTz ? iso : `${iso}Z`);
};

@SignalrListener
@service(TripManager)
export class TripManager {
  @inject(Signalr) protected signalr: Signalr;
  @inject(Logger) protected logger: Logger;
  @inject(TripStore) protected tripStore: TripStore;
  @inject(VehiclePositionStore) protected vehiclePositionStore: VehiclePositionStore;
  @inject(TableManager) protected tableManager!: TableManager;
  @inject(ConfigurationManager) protected configurationManager: ConfigurationManager;
  @inject(AttachmentManager) protected attachmentManager: AttachmentManager;
  @inject(ProQAScriptManager) protected proQaScriptManager: ProQAScriptManager;
  @inject(FilterService) protected filterService: FilterService;
  @inject(Connection) protected connection: Connection;
  @inject(UserController) protected userController: UserController;

  protected loaded = false;

  get hasLoaded() {
    return this.loaded;
  }

  private leadTimeTimer: ReturnType<typeof setTimeout> | null = null;

  async onTripReqMsg(tripId: string): Promise<ITrip | null> {
    try {
      const response = await this.signalr.invoke(TripListReqMsg, {
        id: tripId,
      });
      if (!response) {
        return null;
      }
      const normalizedTrip = new Trip(response);
      return normalizedTrip;
    }
    catch (error) {
      this.logger.error('Error fetching trip:', error);
      return null;
    }
  }

  @SignalrMessage('TripUpdateMsg')
  protected async onTripUpdateMsg(message: ITripUpdateMsg) {
    //  Measure status change round-trip lag (time from UI request to server confirmation)
    const pending = consumeStatusChangeTiming(message.id);
    if (pending) {
      this.logger.track('resource_list.status_update_lag', {
        durationMs: Math.round(performance.now() - pending.startMs),
        toStatus: pending.toStatusId,
        ...(pending.toStatusLabel !== undefined && { toStatusLabel: pending.toStatusLabel }),
      });
    }

    this.update([message]);
  }

  @SignalrMessage('TripListRespMsg')
  protected async onTripListRespMsg(message: ITripListRespMsg) {
    this.update(message.trips);
  }

  find(tripId: string): ITrip | undefined {
    return this.tripStore.find(tripId);
  }

  reset() {
    this.loaded = false;
    this.tripStore.set([]);
  }

  // Remove this method once we start getting VehiclePositionHistoryMsg
  private cleanupVehiclePositionsForUnits(unitVehicleIds: Iterable<string>) {
    // Reuse the assignedTripsByUnit index — no additional iteration needed.
    const activeUnitIds = this.assignedTripsByUnit;

    for (const unitVehicleId of unitVehicleIds) {
      if (!activeUnitIds.has(unitVehicleId)) {
        this.vehiclePositionStore.remove(unitVehicleId);
      }
    }
  }

  update(trips: Trip[]) {
    this.loaded = true;
    const affectedUnitVehicleIds = new Set<string>();
    const normalizedTrips: Trip[] = [];
    const existingTripsMap = this.tripStore.toMap();

    trips.forEach((trip) => {
      affectedUnitVehicleIds.add(trip.unitId ?? '');
      const existingTrip = existingTripsMap.get(trip.id);
      normalizedTrips.push(existingTrip ? normalizeObjects(existingTrip, trip) : normalize(trip));

      if (Number(trip.id) > 0) {
        if (trip.attachments && trip.attachments.length > 0) {
          this.attachmentManager.populateExistingAttachments(trip.attachments);
        }
      }

      if (trip.responderScript) {
        this.proQaScriptManager.update(
          {
            tripId: trip.id,
            determinantCode: trip.determinantCode ?? '',
            responderScript: trip.responderScript,
          },
        );
      }
    });

    this.tripStore.batchUpdate(normalizedTrips);

    this.cleanupVehiclePositionsForUnits(affectedUnitVehicleIds);
    this.scheduleNextLeadTimeCheck();
  }

  get all(): Trip[] {
    return this.tripStore.all();
  }

  isTripWithinLeadTime(trip: ITrip) {
    const leadTime = this.configurationManager.find(constants.CONFIGURATIONS.OPEN_WORK_LEAD_TIME)?.value;
    const leadTimeValue = leadTime ? Number(leadTime) : 0;
    const pickup = DateTime.fromISO(trip.pickupDateTime, { zone: 'utc' });
    const current = DateTime.fromJSDate(new Date(), { zone: 'utc' });
    const timeDiffInMinutes = Math.floor(pickup.diff(current).shiftTo('minutes').minutes);
    return timeDiffInMinutes < leadTimeValue;
  }

  private pendingAssignmentsRef?: ComputedRef<ITrip[]>;

  get getPendingAssignments(): ITrip[] {
    this.pendingAssignmentsRef ??= computed(() => this.computePendingAssignments());
    return this.pendingAssignmentsRef.value;
  }

  private computePendingAssignments(): ITrip[] {
    const startMs = performance.now();

    // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
    this.tripStore.leadTimeTick;

    const leadTime = this.configurationManager.find(constants.CONFIGURATIONS.OPEN_WORK_LEAD_TIME)?.value;
    const leadTimeMinutes = leadTime ? Number(leadTime) : 0;
    const now = Date.now();
    const leadTimeWindow = leadTimeMinutes * 60_000;

    const isWithinLeadTime = leadTimeMinutes > 0
      ? (trip: ITrip) => (parseUtc(trip.pickupDateTime) - now) < leadTimeWindow
      : () => true;

    let result: ITrip[] = [];
    const table = this.tableManager.findTable(TABLE.PENDING_ASSIGNMENT);
    const tripStatusFilter = table?.filters?.find((filter) => filter.field === FILTER_FIELD.T_STATUS)?.value;

    if (tripStatusFilter === TRIPS.STATUS.WILL_CALL) {
      result = this.all.filter(
        (trip) => trip.tripStatus === TRIPS.STATUS.OPEN && isWithinLeadTime(trip),
      );
    }
    else {
      result = this.all.filter(
        (trip) => (trip.tripStatus === TRIPS.STATUS.OPEN ||
            trip.tripStatus === TRIPS.STATUS.WILL_CALL) && isWithinLeadTime(trip),
      );
    }

    for (const trip of result) {
      if (!trip.builtTime) {
        const createdStatus = trip.statusTimes.find((time) => time.status.id === TRIPS.STATUS.CREATED);
        trip.builtTime = createdStatus?.timestamp ?? '';
      }
    }

    const filtered = this.filterService.applyActiveFilter(result);

    //  Track pending assignments computation if it's notably slow (>20ms)
    const durationMs = performance.now() - startMs;
    if (durationMs > 20) {
      this.logger.track('pending_assignments.compute', {
        durationMs: Math.round(durationMs),
        totalTrips: this.all.length,
        pendingCount: filtered.length,
      });
    }

    return filtered;
  }

  scheduleNextLeadTimeCheck() {
    if (this.leadTimeTimer) {
      clearTimeout(this.leadTimeTimer);
      this.leadTimeTimer = null;
    }

    const leadTime = this.configurationManager.find(constants.CONFIGURATIONS.OPEN_WORK_LEAD_TIME)?.value;
    const leadTimeMinutes = leadTime ? Number(leadTime) : 0;
    if (leadTimeMinutes <= 0) return;

    const now = Date.now();
    const leadTimeWindow = leadTimeMinutes * 60_000;
    let earliest = Infinity;

    for (const trip of this.all) {
      if (trip.tripStatus === TRIPS.STATUS.OPEN || trip.tripStatus === TRIPS.STATUS.WILL_CALL) {
        const timeUntilEnter = parseUtc(trip.pickupDateTime) - now - leadTimeWindow;
        if (timeUntilEnter > 0 && timeUntilEnter < earliest) {
          earliest = timeUntilEnter;
        }
      }
    }

    if (earliest < Infinity) {
      this.leadTimeTimer = setTimeout(() => {
        this.tripStore.leadTimeTick = Date.now();
        this.scheduleNextLeadTimeCheck();
      }, earliest);
    }
  }

  // Builds unit→assigned-trips index once per trip-store change (O(trips)),
  // instead of scanning all trips per unit (O(units × trips)).
  private get assignedTripsByUnit(): Map<string, ITrip[]> {
    const index = new Map<string, ITrip[]>();
    this.tripStore.forEach((trip) => {
      if (!isComplete(trip) && !isCanceled(trip) && !isUnassigned(trip) && isAssignedOrPreAssigned(trip)) {
        const unitId = trip.unitId ?? '';
        let list = index.get(unitId);
        if (!list) {
          list = [];
          index.set(unitId, list);
        }
        list.push(trip);
      }
    });
    // Sort each unit's trips by pickupDateTime once.
    index.forEach((trips) => {
      trips.sort((a, b) => (a.pickupDateTime ?? '').localeCompare(b.pickupDateTime ?? ''));
    });
    return index;
  }

  getAssignedTripsByUnitId(unitVehicleId: string, unitShiftId?: string): ITrip[] {
    const trips = this.assignedTripsByUnit.get(unitVehicleId) ?? [];
    // Shift ownership lives in a util so it can be unit tested on its own (ZD-20527).
    return trips.filter((trip) => belongsToUnitShift(trip, unitShiftId));
  }

  getPrimaryAssignedTripByUnitId(unitVehicleId: string, unitShiftId?: string): ITrip | undefined {
    const trips = this.getAssignedTripsByUnitId(unitVehicleId, unitShiftId);
    return trips.length ? trips[0] : undefined;
  }

  getTripNotesCount = (trip: ITrip) => trip.tripNotesCount;

  async cancelTrip(params: CancelTripParams): Promise<void> {
    const { isPortal } = this.connection;
    const portalFields = isPortal
      ? {
        requesterKey: this.userController.activeUser.tenantKey,
        requesterName: this.userController.activeUser.tenantName,
        tripSource: APP.PORTAL_TITLE_SHORT,
      }
      : {
        requesterKey: '',
        requesterName: '',
        tripSource: APP.TITLE,
      };

    await cancelTrip(
      this.signalr,
      this.logger,
      { ...params, ...portalFields },
    );
  }

  async assignUnitToTrip(params: AssignUnitToTripParams): Promise<void> {
    await assignUnitToTrip(params);
  }

  async changeUnitStatus(params: ChangeUnitStatusParams): Promise<void> {
    await changeUnitStatus(params);
  }

  async duplicateTrip(params: DuplicateTripParams): Promise<void> {
    await duplicateTrip(params);
  }

  async unassignTrip(params: UnassignTripParams): Promise<void> {
    await unassignTrip(params);
  }

  async updatePatientWithTrip(params: UpdatePatientWithTripParams): Promise<void> {
    await updatePatientWithTrip(params);
  }

  async updateTripTimestamps(params: UpdateTripTimestampsParams): Promise<void> {
    await updateTripTimestamps(this.signalr, this.logger, params);
  }

  async createTrip(params: CreateTripParams): Promise<unknown> {
    return createTrip(params);
  }

  async createReturnTrip(params: CreateReturnTripParams): Promise<void> {
    await createReturnTrip(params);
  }

  async createRecurringTrip(params: CreateRecurringTripParams): Promise<unknown> {
    return createRecurringTrip(params);
  }

  checkForRequiredLateReasons(data: ITripActionData): boolean {
    return checkForRequiredLateReasons(data);
  }

  async tryUnitAction(data: ITripActionData, dispatch?: DispatchCallback) {
    return tryUnitAction(data, dispatch);
  }

  async tryTripCreateAssignAction(data: ITripActionData, dispatch?: DispatchCallback) {
    return tryTripCreateAssignAction(data, dispatch);
  }

  async updateLateReason(data: ITripActionData, dispatch?: DispatchCallback): Promise<void> {
    return updateLateReason(data, dispatch);
  }
}
